import {
  DEFAULT_BREAK_DURATION_IN_MINUTES,
  DEFAULT_BREAK_POLICY_MIN_SHIFT_DURATION_IN_MINUTES,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, FormControl, MenuItem, Select, type SelectChangeEvent, Stack } from "@mui/material";
import { type BreakPolicyRule } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import isEqual from "lodash/isEqual";
import { useEffect, useMemo, useState } from "react";
import { Prompt } from "react-router-dom";

import { Button } from "../../../../components/Button";
import { IconButton } from "../../../../components/IconButton";
import { LoadingButton } from "../../../../components/LoadingButton";
import { Text } from "../../../../components/Text";
import {
  BREAK_DURATION_MINUTE_OPTIONS,
  MAX_BREAK_POLICY_RULES,
  SHIFT_DURATION_HOUR_OPTIONS,
} from "./constants";
import { sortBreakPolicyRules, validateBreakPolicyRules } from "./utils";

type BreakPolicyRuleDraft = Partial<BreakPolicyRule>;

type BreakRulePlaceholder = (typeof BREAK_RULE_PLACEHOLDERS)[number];

const BREAK_RULE_PLACEHOLDERS = ["shiftHours", "breakMinutes"] as const;

interface BreakPolicyEditorProps {
  savedRules: BreakPolicyRule[] | undefined;
  isSaving: boolean;
  onSave: (rules: BreakPolicyRule[]) => Promise<boolean>;
}

function buildInitialRules(savedRules: BreakPolicyRule[] | undefined): BreakPolicyRuleDraft[] {
  if (isDefined(savedRules) && savedRules.length > 0) {
    return sortBreakPolicyRules(savedRules);
  }

  return [
    {
      minShiftDurationInMinutes: DEFAULT_BREAK_POLICY_MIN_SHIFT_DURATION_IN_MINUTES,
      breakDurationInMinutes: DEFAULT_BREAK_DURATION_IN_MINUTES,
    },
  ];
}

export function BreakPolicyEditor({ savedRules, isSaving, onSave }: BreakPolicyEditorProps) {
  const { tu } = useTu(I18N_NAMESPACES.settings);
  const [rules, setRules] = useState<BreakPolicyRuleDraft[]>(() => buildInitialRules(savedRules));
  const [error, setError] = useState<string | undefined>(undefined);
  const unsavedChangesMessage = tu(
    "You have unsaved break policy changes. Are you sure you want to leave?"
  );

  // Re-sync local state from server data after save / external refetch.
  useEffect(() => {
    setRules(buildInitialRules(savedRules));
  }, [savedRules]);

  const isDirty = useMemo(
    () => !isEqual(rules, buildInitialRules(savedRules)),
    [rules, savedRules]
  );

  // Warn on tab close / refresh / browser nav while there are unsaved changes.
  // In-app route changes are guarded by <Prompt> below.
  useEffect(() => {
    if (!isDirty) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = unsavedChangesMessage;
      return unsavedChangesMessage;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty, unsavedChangesMessage]);

  const updateRule = (index: number, newRule: Partial<BreakPolicyRuleDraft>) => {
    setRules((previous) =>
      previous.map((rule, draftIndex) => (draftIndex === index ? { ...rule, ...newRule } : rule))
    );
    setError(undefined);
  };

  const addRule = () => {
    setRules((previous) => [...previous, {}]);
    setError(undefined);
  };

  const removeRule = (index: number) => {
    setRules((previous) => previous.filter((_, ruleIndex) => ruleIndex !== index));
    setError(undefined);
  };

  const handleSave = async () => {
    const result = validateBreakPolicyRules(rules);
    if (!result.ok) {
      setError(result.message);
      return;
    }

    setError(undefined);
    await onSave(result.rules);
  };

  const extendedRules = rules.slice(1);

  return (
    <Stack spacing={3}>
      <Prompt when={isDirty} message={unsavedChangesMessage} />
      <RuleFormRow
        rule={rules[0]}
        disabled={isSaving}
        onChange={(partial) => updateRule(0, partial)}
      />

      <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
        {tu(
          "Add any additional breaks for longer shift lengths here. When selected, workers will be required to take the set break length for the shift length defined below."
        )}
      </Text>

      {isDefined(error) && (
        <Alert severity="error" role="alert">
          {error}
        </Alert>
      )}

      {extendedRules.map((draft, extendedIndex) => {
        const index = extendedIndex + 1;
        return (
          <RuleFormRow
            key={index}
            rule={draft}
            disabled={isSaving}
            onChange={(partial) => updateRule(index, partial)}
            onRemove={() => removeRule(index)}
          />
        );
      })}

      <Stack direction="row">
        <Button
          variant="outlined"
          size="small"
          disabled={rules.length >= MAX_BREAK_POLICY_RULES || isSaving}
          onClick={addRule}
        >
          {tu("Add Extended Break Rule")}
        </Button>
      </Stack>

      <Stack direction="row">
        <LoadingButton isLoading={isSaving} variant="primary" size="small" onClick={handleSave}>
          {tu("Save")}
        </LoadingButton>
      </Stack>
    </Stack>
  );
}

interface RuleFormRowProps {
  rule: BreakPolicyRuleDraft;
  disabled: boolean;
  onChange: (partial: Partial<BreakPolicyRuleDraft>) => void;
  onRemove?: () => void;
}

function RuleFormRow({ rule, disabled, onChange, onRemove }: RuleFormRowProps) {
  const { tu } = useTu(I18N_NAMESPACES.settings);
  const handleShiftChange = (event: SelectChangeEvent<number | string>) => {
    const { value } = event.target;
    onChange({
      minShiftDurationInMinutes: value === "" ? undefined : Math.round(Number(value) * 60),
    });
  };

  const handleBreakChange = (event: SelectChangeEvent<number | string>) => {
    const { value } = event.target;
    onChange({ breakDurationInMinutes: value === "" ? undefined : Number(value) });
  };

  const shiftHoursValue = isDefined(rule.minShiftDurationInMinutes)
    ? rule.minShiftDurationInMinutes / 60
    : "";
  const breakValue = rule.breakDurationInMinutes ?? "";
  const tokens = tokenizeBreakRuleTemplate(
    tu("For shifts that exceed {{shiftHours}} hours, required break is {{breakMinutes}} minutes")
  );

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Stack
        useFlexGap
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        flexWrap="wrap"
        sx={{ flex: 1, minWidth: 0 }}
      >
        {tokens.map((token) => {
          if (token.type === "text") {
            return (
              <Text key={token.key} variant="body2">
                {token.value}
              </Text>
            );
          }

          if (token.type === "shiftHours") {
            return (
              <ShiftHoursSelect
                key={token.key}
                disabled={disabled}
                value={shiftHoursValue}
                onChange={handleShiftChange}
              />
            );
          }

          return (
            <BreakMinutesSelect
              key={token.key}
              disabled={disabled}
              value={breakValue}
              onChange={handleBreakChange}
            />
          );
        })}
      </Stack>
      {isDefined(onRemove) && (
        <IconButton
          aria-label={tu("Remove break rule")}
          iconType="close"
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={onRemove}
        />
      )}
    </Stack>
  );
}

function ShiftHoursSelect(props: {
  disabled: boolean;
  value: number | "";
  onChange: (event: SelectChangeEvent<number | string>) => void;
}) {
  const { tu } = useTu(I18N_NAMESPACES.settings);

  return (
    <FormControl size="small" sx={{ "& .MuiSelect-select": { paddingY: 0 } }}>
      <Select
        displayEmpty
        disabled={props.disabled}
        inputProps={{ "aria-label": tu("Shift length in hours") }}
        value={props.value}
        onChange={props.onChange}
      >
        <MenuItem disabled value="">
          <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            {tu("Select")}
          </Text>
        </MenuItem>
        {SHIFT_DURATION_HOUR_OPTIONS.map((hours) => (
          <MenuItem key={hours} value={hours}>
            {Number.isInteger(hours) ? `${hours}` : hours.toFixed(1)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function BreakMinutesSelect(props: {
  disabled: boolean;
  value: number | "";
  onChange: (event: SelectChangeEvent<number | string>) => void;
}) {
  const { tu } = useTu(I18N_NAMESPACES.settings);

  return (
    <FormControl size="small" sx={{ "& .MuiSelect-select": { paddingY: 0 } }}>
      <Select
        displayEmpty
        disabled={props.disabled}
        inputProps={{ "aria-label": tu("Break length in minutes") }}
        value={props.value}
        onChange={props.onChange}
      >
        <MenuItem disabled value="">
          <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            {tu("Select")}
          </Text>
        </MenuItem>
        {BREAK_DURATION_MINUTE_OPTIONS.map((minutes) => (
          <MenuItem key={minutes} value={minutes}>
            {minutes}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

type BreakRuleTemplateToken =
  | { key: string; type: "text"; value: string }
  | { key: string; type: BreakRulePlaceholder };

function tokenizeBreakRuleTemplate(template: string): BreakRuleTemplateToken[] {
  return getBreakRuleTemplateTokens(template);
}

function getBreakRuleTemplateTokens(template: string): BreakRuleTemplateToken[] {
  const tokens: BreakRuleTemplateToken[] = [];
  const pattern = /\{\{(shiftHours|breakMinutes)\}\}/g;
  let lastIndex = 0;
  let match = pattern.exec(template);

  while (match !== null) {
    const [placeholderText, placeholderName] = match;
    const text = template.slice(lastIndex, match.index).trim();

    if (text.length > 0) {
      tokens.push({ key: `text-${tokens.length}`, type: "text", value: text });
    }

    tokens.push({
      key: `${placeholderName}-${tokens.length}`,
      type: placeholderName as BreakRulePlaceholder,
    });

    lastIndex = match.index + placeholderText.length;
    match = pattern.exec(template);
  }

  const trailingText = template.slice(lastIndex).trim();
  if (trailingText.length > 0) {
    tokens.push({ key: `text-${tokens.length}`, type: "text", value: trailingText });
  }

  return tokens;
}
